/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { Logger } from '@quickplay/foundation';
import {
  GodavariApplicationMetadata,
  GodavariApplicationMetadataInput,
  GodavariMetadata,
  GodavariMetadataInput,
} from '../model/GodavariPayload';
import {
  GODAVARI_ENTITLEMENT_FIELDS,
  GodavariIdentityUpdate,
  getGodavariIdentityUpdate,
  normalizeGodavariMetadata,
} from '../../util/GodavariMetadataUtil';

/** Application identity survives playback resets; current playback measurements do not. */
export class GodavariMetadataStore {
  playback: GodavariMetadata;
  readonly application: GodavariApplicationMetadata;
  private readonly playbackDefaults: GodavariMetadataInput;

  constructor(
    applicationMetadata?: GodavariApplicationMetadataInput,
    playbackDefaults?: GodavariMetadataInput,
    private readonly logger?: Logger,
  ) {
    const normalized = normalizeGodavariMetadata(applicationMetadata ?? {}, logger);
    this.application = Object.fromEntries(
      Object.entries(normalized).filter(([key]) => key.startsWith('app_') || key === 'custom_tags'),
    );
    this.playbackDefaults = { ...playbackDefaults };
    this.playback = normalizeGodavariMetadata(this.playbackDefaults, logger);
    this.applyIdentityDefaults();
  }

  resetPlayback(): void {
    this.playback = normalizeGodavariMetadata(this.playbackDefaults, this.logger);
  }

  apply(metadata: object): void {
    this.applyNormalized(normalizeGodavariMetadata(metadata, this.logger), getGodavariIdentityUpdate(metadata));
  }

  applyNormalized(normalized: GodavariMetadata, identity?: GodavariIdentityUpdate): void {
    const userChanged = normalized.app_user_id !== undefined && normalized.app_user_id !== this.application.app_user_id;
    const userRemoved = identity?.removeUser ?? false;
    const profileChanged =
      normalized.app_profile_id !== undefined && normalized.app_profile_id !== this.application.app_profile_id;
    const profileRemoved = identity?.removeProfile ?? false;
    if (userChanged || userRemoved || profileChanged || profileRemoved) {
      this.invalidateEntitlements(normalized);
    }
    this.replaceIdentity(normalized, userChanged || userRemoved, userRemoved, profileRemoved);
    this.playback = {
      ...this.playback,
      ...normalized,
      ...(normalized.custom_tags ? { custom_tags: { ...this.playback.custom_tags, ...normalized.custom_tags } } : {}),
    };
    for (const [key, value] of Object.entries(normalized)) {
      if (key.startsWith('app_')) {
        Object.assign(this.application, { [key]: value });
      }
    }
    // An unmapped user type must not retain a previous user's subscription.
    if (identity?.clearSubscription && normalized.app_subscribe_type === undefined) {
      this.removeIdentityField('app_subscribe_type');
    }
    this.applyIdentityDefaults();
  }

  /**
   * Like the in-market web app, liv_id is the device id unless the app provides one, and the device id
   * stands in for a missing user id. Both are restored after a user change clears the previous values.
   */
  private applyIdentityDefaults(): void {
    const deviceId = this.application.app_device_id;
    if (!deviceId) return;
    this.application.app_sliv_profile_id ??= deviceId;
    this.application.app_user_id ??= deviceId;
  }

  private invalidateEntitlements(replacement: GodavariMetadata): void {
    for (const key of GODAVARI_ENTITLEMENT_FIELDS) {
      delete this.playbackDefaults[key];
      if (replacement[key] === undefined) {
        delete this.playback[key];
      }
    }
    for (const key of ['app_ppid', 'app_sliv_profile_id'] as const) {
      if (replacement[key] === undefined) {
        delete this.playback[key];
        // Mask configuration defaults as well, including after the next playback reset.
        this.application[key] = undefined;
      }
    }
  }

  private replaceIdentity(
    replacement: GodavariMetadata,
    userChanged: boolean,
    userRemoved: boolean,
    profileRemoved: boolean,
  ): void {
    if (userRemoved) {
      this.removeIdentityField('app_user_id');
    }
    if ((userChanged && replacement.app_profile_id === undefined) || profileRemoved) {
      this.removeIdentityField('app_profile_id');
    }
    if (userChanged && replacement.app_subscribe_type === undefined) {
      this.removeIdentityField('app_subscribe_type');
    }
  }

  private removeIdentityField(key: 'app_user_id' | 'app_profile_id' | 'app_subscribe_type'): void {
    delete this.playback[key];
    delete this.application[key];
  }
}
